import React from 'react';
import { TextV2, SpacingStack, ButtonV2, TooltipV2, Box } from '@withjoy/joykit';
import { DrawerV2 } from '@withjoy/joykit/components/DrawerV2';
import { LockHeavy } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core/i18n';
import { styles, StyledLinksContainer, StyledMenuLink, StyledMenuTitle, drawerOverrides, BottomStackStyled, StyledMenuLinkBox, StyledProfileButton } from './EventMenu.styles';
import BatchCard from './components/BatchCard/BatchCard';
import MenuButton from './components/MenuButton/MenuButton';
import { useEventMenuController } from '@apps/guest/packages/layout-engine/components/EventMenu/EventMenu.controller';
import { Link } from '@react-router';
import { customScrollbarCss } from '@shared/utils/style/scrollbar';
import { useTheme } from '@withjoy/joykit';
import chroma from 'chroma-js';
import type { EventMenuProps } from './EventMenu.types';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { getNativeRegistryHardNavUrl } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body2ToButtonsOverride,
  body2ToNavigationOverride,
  button2ToButtonsOverride,
  display3ToEventTitleOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { Category } from '@graphql/generated-base';
import { pxToRem } from '@withjoy/joykit/theme';
import { useScaleGuestSiteFontSize } from '@shared/core/website/theming/useScaleGuestSiteFontSize';
import { useSmartAppBannerProvider } from '@shared/components/SmartAppBanner';
import { useStickyNavBarOffset } from '@apps/guest/packages/layout-engine/components/StickyNavBarOffset';
import { useMobileTopBarNavOffset } from '@apps/guest/packages/layout-engine/components/MobileTopBarNav';
import { Portal } from 'react-portal';
import { GUEST_SITE_START_PORTAL_ID } from '@apps/guest/routes/GuestSite/GuestSite.constants';
import { withWindow } from '@shared/utils/withWindow';
import { PreviewDisabledTooltipContent } from '@apps/guest/packages/layout-engine/components/PreviewDisabledTooltipContent/PreviewDisabledTooltipContent';
import { usePreviewIframeScale } from '@apps/guest/packages/layout-engine/components/PreviewDisabledTooltipContent/usePreviewIframeScale';

const EventMenu: React.FC<EventMenuProps> = ({
  title,
  eventProps,
  eventHandle,
  isCustomPage,
  hideMenuButton,
  backgroundColor,
  handleMenuClicked,
  handleMenuOpenedChange,
  isOpen: externalIsOpen,
  onOpen: externalOnOpen,
  onClose: externalOnClose,
  isSinglePage,
}) => {
  const {
    isAdmin,
    isOpen: internalIsOpen,
    onOpen: internalOnOpen,
    onClose: internalOnClose,
    name,
    email,
    initials,
    unlockDialogHandle,
    rsvpHandle,
    pages,
    shouldDisplayRSVPButton,
    shouldDisplayUnlockButton,
    onProfileHandle,
    buttonNotYouText,
    logoutHandle,
    isProfile,
    handleClickItemMenu,
    unlockButtonText,
    rsvpButtonText,
  } = useEventMenuController({
    eventHandle,
    handleMenuClicked,
    handleMenuOpenedChange,
    eventProps,
    onClose: externalOnClose,
  });

  const isOpen = externalIsOpen ?? internalIsOpen;
  const onOpen = externalOnOpen ?? internalOnOpen;
  const onClose = externalOnClose ?? internalOnClose;

  const { tq } = useTranslation('eventMenu');

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const eventPageRoute = useEventPageRoute(eventHandle, pages);
  const queryString = useQueryString();
  const [useRegistryLinksNav, isRegistryLinksNavLoading] = useIsFeatureEnabled('registryEnableGuestNavRegistryLinks');
  const isPreviewing = isInIframe();
  const previewScale = usePreviewIframeScale(isPreviewing);
  const { smartAppBannerHeight } = useSmartAppBannerProvider();
  const { offset } = useStickyNavBarOffset();
  const mobileTopBarNavOffset = useMobileTopBarNavOffset();
  const totalTopOffset = Math.max(smartAppBannerHeight, offset) + mobileTopBarNavOffset;

  const theme = useTheme();
  const linkTextColor = theme.colors.linkText;
  const colorLinkHoverBackground = chroma(linkTextColor).alpha(0.1).hex();
  const colorLinkActiveBackground = chroma(linkTextColor).alpha(0.2).hex();

  return (
    <>
      {!hideMenuButton && (
        <Portal node={withWindow(global => global.document.getElementById(GUEST_SITE_START_PORTAL_ID) || global.document.body, null)}>
          <MenuButton
            onClick={onOpen}
            isCustomPage={isCustomPage}
            topOffset={totalTopOffset}
          />
        </Portal>
      )}
      <DrawerV2
        anchor={'left'}
        disableAutoFocus
        overrides={drawerOverrides(totalTopOffset)}
        isOpen={isOpen}
        onClose={onClose}
        useBackdrop={true}
      >
        <DrawerV2.CloseButton />
        <DrawerV2.Body __css={styles.drawerBody}>
          <SpacingStack
            css={customScrollbarCss}
            __css={styles.bodyStack}
            backgroundColor={backgroundColor}
          >
            <StyledLinksContainer __css={styles.linksContainer}>
              <GuestSiteTypographyOverride override={display3ToEventTitleOverride}>
                <StyledMenuTitle
                  {...styles.menuTitle}
                  typographyVariant="display3"
                  fontSize={scaleFontSize(pxToRem(36), Category.EVENT_TITLE)}
                >
                  {title}
                </StyledMenuTitle>
              </GuestSiteTypographyOverride>
              {pages
                ?.filter(page => page.pageSlug !== 'rsvp' && page.pageSlug !== 'guests')
                .map(page => {
                  const isLegacyPage = page.pageSlug === 'moments';
                  const isDisabledInPreview = isLegacyPage && isPreviewing;
                  const linkContent = (
                    <StyledMenuLinkBox
                      hoverBackground={colorLinkHoverBackground}
                      activeBackground={colorLinkActiveBackground}
                      cursor={isDisabledInPreview ? 'default' : undefined}
                    >
                      <GuestSiteTypographyOverride override={body2ToNavigationOverride}>
                        <StyledMenuLink {...styles.links}>{page.pageTitle}</StyledMenuLink>
                      </GuestSiteTypographyOverride>
                    </StyledMenuLinkBox>
                  );

                  if (isDisabledInPreview) {
                    const pagePath = eventPageRoute.goToPath(page.type, page.pageSlug);
                    return (
                      <TooltipV2
                        key={page.id}
                        onMouseEnterDelay={500}
                        content={
                          <PreviewDisabledTooltipContent
                            href={pagePath}
                            previewScale={previewScale}
                            maxWidth={240}
                          />
                        }
                      >
                        <div style={styles.linkItem}>{linkContent}</div>
                      </TooltipV2>
                    );
                  }

                  // While the remote flag is resolving, bias toward the SPA path (skip the hard-nav
                  // helper) so a fast click on treatment users doesn't land them in /registry.
                  const registryHardNavUrl =
                    useRegistryLinksNav || isRegistryLinksNavLoading ? undefined : getNativeRegistryHardNavUrl(page.type, isSinglePage ?? true, eventHandle, queryString);
                  if (registryHardNavUrl) {
                    return (
                      <a
                        onClick={() => handleClickItemMenu(page.pageSlug)}
                        style={styles.linkItem}
                        key={page.id}
                        href={registryHardNavUrl}
                      >
                        {linkContent}
                      </a>
                    );
                  }
                  return (
                    <Link
                      onClick={() => handleClickItemMenu(page.pageSlug)}
                      style={styles.linkItem}
                      key={page.id}
                      to={eventPageRoute.goToPath(page.type, page.pageSlug)}
                    >
                      {linkContent}
                    </Link>
                  );
                })}
            </StyledLinksContainer>
            <BottomStackStyled isPreviewing={isPreviewing}>
              {shouldDisplayUnlockButton && (
                <GuestSiteTypographyOverride override={body2ToButtonsOverride}>
                  <ButtonV2
                    onClick={unlockDialogHandle}
                    intent="primary"
                    variant="outline"
                    startIcon={<LockHeavy />}
                  >
                    <TextV2>{unlockButtonText}</TextV2>
                  </ButtonV2>
                </GuestSiteTypographyOverride>
              )}

              {shouldDisplayRSVPButton && (
                <GuestSiteTypographyOverride override={button2ToButtonsOverride}>
                  {isPreviewing ? (
                    <Box pointerEvents="auto">
                      <TooltipV2
                        onMouseEnterDelay={500}
                        content={
                          <PreviewDisabledTooltipContent
                            href={`/${eventHandle}/rsvp`}
                            previewScale={previewScale}
                            maxWidth={240}
                          />
                        }
                      >
                        <ButtonV2
                          variant="outline"
                          intent="primary"
                          shape="rounded"
                          cursor="default"
                          opacity={0.6}
                          width="100%"
                        >
                          {rsvpButtonText}
                        </ButtonV2>
                      </TooltipV2>
                    </Box>
                  ) : (
                    <ButtonV2
                      onClick={rsvpHandle}
                      variant="outline"
                      intent="primary"
                      shape="rounded"
                    >
                      {rsvpButtonText}
                    </ButtonV2>
                  )}
                </GuestSiteTypographyOverride>
              )}

              {isAdmin && (
                <ButtonV2
                  onClick={() => handleMenuClicked && handleMenuClicked('EditEvent')}
                  intent="primary"
                  size="sm"
                  typographyVariant="button1"
                  variant="outline"
                  as={Link}
                  to={`/${eventHandle}/edit/dashboard`}
                  shape="rounded"
                >
                  {isPreviewing ? tq`Edit Website` : tq`Edit Event`}
                </ButtonV2>
              )}

              {initials && (
                <div>
                  <StyledProfileButton
                    {...(!isProfile && { as: 'div' as const })}
                    variant={'link'}
                    onClick={isProfile ? onProfileHandle : undefined}
                  >
                    <BatchCard
                      initials={`${initials}`}
                      name={`${name}`}
                      email={`${email}`}
                    />
                  </StyledProfileButton>
                  {!isAdmin && (
                    <ButtonV2
                      variant={'link'}
                      typographyVariant={'label2'}
                      onClick={logoutHandle}
                      {...styles.logoutButton}
                    >
                      {buttonNotYouText}
                    </ButtonV2>
                  )}
                </div>
              )}
            </BottomStackStyled>
          </SpacingStack>
        </DrawerV2.Body>
      </DrawerV2>
    </>
  );
};

export default EventMenu;
